import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {LoadedMessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {MessageSourcePanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {AsyncLoader} from '../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {center, stretch} from '../../../libs/shared/ui/src/styles/mixins';
import {ExpandedDarkThemeProvider} from '../../../libs/shared/ui/src/theme/expandedDarkThemeProvider';
import {useAppDispatch} from '../../helpers/appReduxHelpers';
import {retryMessageSourceAsyncAction} from '../../store/panels/messageSource/messageSourcePanelActions';
import {Frame} from '../common/frames/frame';
import {FrameTitle} from '../common/frames/frameTitle';

/*
 * Props.
 */

interface PopupMessageSourceProps {
  panelId: MessageSourcePanelId;
  status: AsyncStatusesEnum;
  message: LoadedMessageModel;
  messageSource: string | undefined;
  onRequestClose: () => void;
}

const StyledFrameDiv = styled.div`
  overflow: auto;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  ${stretch()}
`;

const StyledLoaderDiv = styled.div`
  ${center()}
`;

const StyledMessageSourcePre = styled.pre`
  word-wrap: break-word;
  white-space: pre-wrap;
  margin: 13px 8px;
  font-family: monospace;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  -webkit-user-select: text;
  user-select: text;
`;

/*
 * Component.
 */

export const PopupMessageSource: FC<PopupMessageSourceProps> = (props) => {
  const dispatch = useAppDispatch();
  const onRetryClick = () => dispatch(retryMessageSourceAsyncAction(props.panelId));

  return (
    <ExpandedDarkThemeProvider>
      <Frame
        width={530}
        height={650}
        shouldForcePopout
        onRequestClose={props.onRequestClose}
        interactionId="popup-message-source"
      >
        <FrameTitle>
          <Trans id="message-source-popup-title">Source of {props.message.subject}</Trans>
        </FrameTitle>
        <StyledFrameDiv>{renderContent(props, onRetryClick)}</StyledFrameDiv>
      </Frame>
    </ExpandedDarkThemeProvider>
  );
};

function renderContent(props: PopupMessageSourceProps, onRetryClick: () => void) {
  const {status, messageSource} = props;

  // Loading / Error states.
  if (status !== AsyncStatusesEnum.LOADED || !messageSource) {
    return (
      <StyledLoaderDiv>
        <AsyncLoader status={status} onRetryClick={onRetryClick} />
      </StyledLoaderDiv>
    );
  }

  // Loaded state.
  return <StyledMessageSourcePre>{messageSource}</StyledMessageSourcePre>;
}
